import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { MaterialModule } from '../../../shared/material.module';
import { DirectorFlat } from '../../../models/director';
import { DirectorService } from '../../../services/director.service';
import { DirectorPositionsResponseDto, DirectorPositionDto } from '../../../models/api/directorPositionsDto';
import { DirectorTrainingOrEducationType } from '../../../models/enums/directorTrainingOrEducationType.enum';

interface PositionsByYear {
  year: number;
  positions: DirectorPositionDto[];
}

interface PositionsByCompany {
  companyId: string;
  companyName: string;
  positions: DirectorPositionDto[];
}

@Component({
  selector: 'app-portal-director-view',
  standalone: true,
  imports: [CommonModule, MaterialModule, MatTableModule, NgxSpinnerModule],
  templateUrl: './portal-director-view.component.html',
  styleUrls: ['./portal-director-view.component.scss']
})
export class PortalDirectorViewComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private directorService = inject(DirectorService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);

  director: DirectorFlat | null = null;
  photoUrl: string | null = null;
  positions: DirectorPositionsResponseDto | null = null;
  sortedWorkExperience: any[] = [];

  selectedTabIndex = 0;
  positionsViewMode: 'year' | 'company' = 'year';

  boardPositionsByYear: PositionsByYear[] = [];
  executivePositionsByYear: PositionsByYear[] = [];
  boardPositionsByCompany: PositionsByCompany[] = [];
  executivePositionsByCompany: PositionsByCompany[] = [];

  boardDisplayedColumns: string[] = [
    'companyName', 'classification', 'occupationName', 'tenure',
    'meetingAttendancePercentage', 'forVotesPct', 'againstVotesPct',
    'auditCommitteeRole', 'remunerationCommitteeRole', 'nominationCommitteeRole',
    'cgCommitteeRole', 'shares'
  ];

  boardByCompanyDisplayedColumns: string[] = [
    'year', 'classification', 'occupationName', 'tenure',
    'meetingAttendancePercentage', 'forVotesPct', 'againstVotesPct',
    'auditCommitteeRole', 'remunerationCommitteeRole', 'nominationCommitteeRole',
    'cgCommitteeRole', 'shares'
  ];

  executiveDisplayedColumns: string[] = ['companyName', 'occupationName', 'tenure', 'shares'];
  executiveByCompanyDisplayedColumns: string[] = ['year', 'occupationName', 'tenure', 'shares'];

  workExperienceDisplayedColumns: string[] = ['company', 'position', 'startDate', 'endDate'];
  trainingDisplayedColumns: string[] = ['type', 'educationField', 'university'];

  genderLabels: Record<number, string> = { 0: 'Unknown', 1: 'Male', 2: 'Female' };
  trainingTypeLabels: Record<number, string> = {
    [DirectorTrainingOrEducationType.None]: 'None',
    [DirectorTrainingOrEducationType.BSc]: 'BSc',
    [DirectorTrainingOrEducationType.MSc]: 'MSc',
    [DirectorTrainingOrEducationType.PhD]: 'PhD'
  };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.router.navigate(['/portal/directors']);
      return;
    }

    this.spinnerService.show();
    this.directorService.customerGetDirectorById(id).subscribe({
      next: (director) => {
        this.director = director;
        this.sortedWorkExperience = this.sortWorkExperience(director?.workExperience || []);
        if (director?.photoFileName) {
          this.loadPhoto(id);
        }
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading director:', error);
        this.toast.error('Error loading director');
        this.spinnerService.hide();
      }
    });

    this.directorService.customerGetDirectorPositions(id).subscribe({
      next: (positions) => {
        this.positions = positions;
        this.boardPositionsByYear = this.groupByYear(positions?.boardPositions || []);
        this.executivePositionsByYear = this.groupByYear(positions?.executivePositions || []);
        this.boardPositionsByCompany = this.groupByCompany(positions?.boardPositions || []);
        this.executivePositionsByCompany = this.groupByCompany(positions?.executivePositions || []);
      },
      error: (error) => console.error('Error loading positions:', error)
    });
  }

  private loadPhoto(id: string): void {
    this.directorService.customerGetDirectorPhoto(id).subscribe({
      next: (blob) => {
        if (blob && blob.size > 0) {
          this.photoUrl = URL.createObjectURL(blob);
        }
      },
      error: () => { /* missing photo is non-critical */ }
    });
  }

  ngOnDestroy(): void {
    if (this.photoUrl) {
      URL.revokeObjectURL(this.photoUrl);
    }
  }

  back(): void {
    this.router.navigate(['/portal/directors']);
  }

  // Present first, then most-recent startDate first. Mirrors director-form ordering.
  private sortWorkExperience(items: any[]): any[] {
    return [...items].sort((a, b) => {
      if (!!a.isPresent !== !!b.isPresent) return a.isPresent ? -1 : 1;
      const aTime = a.from ? new Date(a.from).getTime() : 0;
      const bTime = b.from ? new Date(b.from).getTime() : 0;
      return bTime - aTime;
    });
  }

  // Work experience company resolver — flat record uses one of several *Name fields.
  workExperienceCompanyName(we: any): string {
    return we.opbCompanyName || we.companyName || we.workExperienceCompanyName || we.auditFirmName || we.largeShareholderName || '';
  }

  formatDate(value: any): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return '';
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private groupByYear(positions: DirectorPositionDto[]): PositionsByYear[] {
    const map = new Map<number, DirectorPositionDto[]>();
    for (const pos of positions) {
      if (!map.has(pos.year)) map.set(pos.year, []);
      map.get(pos.year)!.push(pos);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([year, items]) => ({
        year,
        positions: items.sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''))
      }));
  }

  private groupByCompany(positions: DirectorPositionDto[]): PositionsByCompany[] {
    const map = new Map<string, { companyName: string; positions: DirectorPositionDto[] }>();
    for (const pos of positions) {
      if (!map.has(pos.companyId)) {
        map.set(pos.companyId, { companyName: pos.companyName, positions: [] });
      }
      map.get(pos.companyId)!.positions.push(pos);
    }
    return Array.from(map.entries())
      .sort(([, a], [, b]) => a.companyName.localeCompare(b.companyName))
      .map(([companyId, data]) => ({
        companyId,
        companyName: data.companyName,
        positions: data.positions.sort((a, b) => a.year - b.year)
      }));
  }

  getClassificationLabel(value?: number): string {
    if (value == null) return '';
    const labels: Record<number, string> = { 0: 'Unknown', 1: 'Executive', 2: 'Non-Executive', 3: 'Independent' };
    return labels[value] ?? '';
  }

  getAuditRoleLabel(value?: number): string {
    if (value == null) return '';
    const labels: Record<number, string> = { 1: 'OK', 2: 'CH', 3: 'FE', 4: 'CH-FE' };
    return labels[value] ?? '';
  }

  getCommitteeRoleLabel(value?: number): string {
    if (value == null) return '';
    const labels: Record<number, string> = { 1: 'OK', 2: 'CH' };
    return labels[value] ?? '';
  }

  formatPercentage(value?: number): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  }

  formatNumber(value?: number): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
}
